import { assert, FilterGroup, ITimeSource, logger, TimeSourceUnsubscribe } from '@fe/core';
import { FlightLayerFacade } from '@fe/flight-layer';
import { FeMap, FeMapPlugin } from '@fe/maplibre-extensions';
import { altitudeGroups, getQualifiedId, products, WeatherProduct } from '@fe/weather';
import { Evented } from 'maplibre-gl';
import { GDFFrame } from '../gdf/type';
import { WeatherLayerManager } from '../manager/weather-layer-manager';
import { DEFAULT_WEATHER_SETTINGS } from '../settings/default';
import { WeatherSettings, WeatherSettingsChangeSource } from '../settings/settings';
import { WeatherLoader } from '../weather-loader';
import { getGlobalDispatcher } from '../worker/dispatcher';
import { WeatherAltitudeSelector } from './altitude-selector';
import {
    ProductsChangedEventTypes,
    SelectedWeatherItem,
    SelectedWeatherItemType,
    WeatherItemUpdateLate,
    WeatherProductFileInfo
} from './types';
import weatherSortFunction from './weather-sort';
import { WeatherTimeline, WeatherTimelineEvents } from './weather-timeline';

// ~ml: TODO: just use event bus I think
export type WeatherLayerPluginEvents = {
    'view.changed': { type: 'view.changed' };
    'settings.changed': { type: 'settings.changed'; settings: WeatherSettings; changeSource: WeatherSettingsChangeSource };
    'products.changed': { type: 'products.changed'; kind: ProductsChangedEventTypes };
    'product.add-remove-visibility': { type: 'product.add-remove-visibility' };
} & WeatherTimelineEvents;

// TODO: extract common bit between all layer stacks
// - authorized codes should be global
export abstract class WeatherLayerPlugin extends Evented<WeatherLayerPluginEvents> implements FeMapPlugin {
    public mapLayerStack: WeatherLayerManager | null = null;

    public readonly timeline: WeatherTimeline;
    public readonly altitudePicker: WeatherAltitudeSelector = new WeatherAltitudeSelector();
    public flightLayerFacade: FlightLayerFacade | null = null;

    protected products: SelectedWeatherItem[] = [];
    private readonly loader = WeatherLoader;
    // TODO: move WMS into @fe/weather, kinda no need because all products are rendered from GDF not WMS
    //protected readonly wms = new WmsService();
    protected settings: WeatherSettings = structuredClone(DEFAULT_WEATHER_SETTINGS);
    private weatherVisibility = true;
    private timeSubscription: TimeSourceUnsubscribe | null = null;

    constructor(
        private readonly workspaceId: number,
        private readonly time: ITimeSource
    ) {
        super();
        this.timeline = new WeatherTimeline(this, this.time);
    }

    // TODO: get rid of this and make this class non abstract I think
    abstract getProductLoadInfo(id: number): WeatherProductFileInfo | null;
    abstract productIsAuthorized(id: number): boolean;

    onAdd(map: FeMap) {
        assert(!this.mapLayerStack);
        // If we ever want to do multiple weather workers this should become a new Dispatcher(webworker_pool)
        this.mapLayerStack = new WeatherLayerManager(this.workspaceId, getGlobalDispatcher());
        this.mapLayerStack.updateSettings(this.settings, WeatherSettingsChangeSource.all);
        this.mapLayerStack.flightLayerFacade = this.flightLayerFacade;
        this.mapLayerStack.addToMap(map, 'fe-weather-slot');
        //this.wms.addToMap(map, 'fe-wms-slot');
        this.timeline.updateSettings(this.settings);
        this.timeline.startAutoUpdate();
        this.timeSubscription = this.time.on((now, sourceChanged) => {
            if (now % 60 && !sourceChanged) {
                return;
            }
            this.calculateLateProducts();
        });
    }

    onRemove() {
        this.mapLayerStack?.removeFromMap();
        this.mapLayerStack = null;
        //this.wms.removeFromMap();
        this.timeSubscription?.();
        this.timeline.stopAnimation();
        this.timeline.stopAutoUpdate();
        this.loader.unloadAll(); // TODO: this will unload all products from the worker when multi map is active!
        this.products = [];
    }

    getSelectedProducts(): SelectedWeatherItem[] {
        return [...this.products];
    }

    addProduct(id: number, type: SelectedWeatherItemType): boolean {
        if (this.products.some((x) => x.id === id && x.type === type)) {
            return false;
        }

        const productInfo = products[id];
        const product: SelectedWeatherItem = {
            id: id,
            visible: true,
            type: type,
            gdf: !shouldDisplayAsWms(productInfo),
            wms: shouldDisplayAsWms(productInfo),
            loaded: false,
            success: false,
            product: productInfo,
            updateLate: WeatherItemUpdateLate.no
        };

        this.products.push(product);
        if (this.settings.other.autoArrangeLayers) {
            // The list should already be sorted so no need to move any other products around
            this.products.sort(weatherSortFunction);
        }

        this.fireProductsChanged('addRemove');
        queueMicrotask(() => this.loadProduct(product));
        return true;
    }

    removeProduct(id: number, type: SelectedWeatherItemType) {
        // Find the product to remove
        const product = this.products.find((x) => x.id === id && x.type === type);

        if (!product) {
            return;
        }

        // Remove it
        this.products = this.products.filter((x) => x !== product);

        // Check if there are other products with this id in the list, this can be due to global or static products
        if (this.products.some((x) => x.id === id)) {
            // If there's still a product with this id in the list that means we removed either global or ud view version of this product
            if (type === SelectedWeatherItemType.global || type === SelectedWeatherItemType.static) {
                // If we remove global find regular version and copy visibility from it to the layer
                const weatherTypeProduct = this.products.find((x) => x.id === id && x.type === SelectedWeatherItemType.weather);
                if (weatherTypeProduct) {
                    // And set the visibility accordingly
                    this.mapLayerStack?.setVisibility(
                        weatherTypeProduct.id,
                        this.weatherVisibility && weatherTypeProduct.visible && weatherTypeProduct.gdf
                    );
                }
            }
            // Otherwise we removed weather version, so do nothing, global or static stays
        } else {
            // There's no other product either global or ud view in the list
            this.mapLayerStack?.removeProduct(id);
            //this.wms.removeProduct(id);
        }

        this.fireProductsChanged('addRemove');
    }

    replaceProducts(
        params: { weather: number[]; hidden: number[]; staticProducts: number[]; weatherVisibility: boolean },
        newSettings: WeatherSettings
    ) {
        assert(this.mapLayerStack);
        this.timeline.stopAnimation();

        const toUnload = this.products.filter((x) => x.type !== SelectedWeatherItemType.global).map((x) => x.id);
        this.loader.unload(toUnload);

        // Clear product list but keep global products
        const keep = this.products.filter((x) => x.type === SelectedWeatherItemType.global);
        const toRemove = this.products.filter((x) => !keep.some((k) => k.id === x.id));

        this.products = keep;
        this.mapLayerStack.removeProducts(toRemove.map((x) => x.id));

        ///this.wms.removeAll();

        this.weatherVisibility = params.weatherVisibility;
        this.settings = newSettings;
        this.altitudePicker.setAltitude(this.settings.altitude.value);

        // If a product belongs to a group it will be encoded in the list as (0xffff + groupId)
        const all = params.weather
            // Everything < 0xffff is a regular product id,
            // check if product or group wasn't removed
            .filter((x) => (x < 0xffff ? x in products : x - 0xffff in altitudeGroups))
            // Map regular product IDs to products and pick altitude for a grouped products
            .map((x) => (x < 0xffff ? products[x] : this.altitudePicker.pick(x - 0xffff)))
            // In case there are products in the list that don't adhere to the new qualified id scheme pick an altitude for those too
            .map((x) => (x?.group == null ? x : this.altitudePicker.pick(x.group)))
            // Filter out product for which we couldn't pick altitude for some reason (shouldn't happen but...)
            .filter(Boolean) as WeatherProduct[];

        // Here we need to make sure that only 1 product of a given group id is present in the list
        // Since ud views were implemented before altitude picker we may have views that contain multiple products form the same group

        const allUniqueGroups: WeatherProduct[] = [];
        for (const product of all) {
            // no group, just continue
            if (product.group === null) {
                allUniqueGroups.push(product);
            } else {
                // if there's no product with this group in the list
                if (!allUniqueGroups.some((x) => x.group === product.group)) {
                    // we can add it
                    allUniqueGroups.push(product);
                }
                // otherwise there's multiple products with the same group
                // so we only show the first one
            }
        }

        this.products.push(
            ...allUniqueGroups.map((product) => ({
                id: product.id,
                visible: !params.hidden.includes(getQualifiedId(product)),
                gdf: !shouldDisplayAsWms(product),
                wms: shouldDisplayAsWms(product),
                type: SelectedWeatherItemType.weather,
                loaded: false,
                success: false,
                product: product,
                updateLate: WeatherItemUpdateLate.no
            }))
        );

        this.products.push(
            ...params.staticProducts.map((x) => ({
                id: x,
                visible: true,
                gdf: true,
                type: SelectedWeatherItemType.static,
                wms: false,
                loaded: false,
                success: false,
                product: products[x],
                updateLate: WeatherItemUpdateLate.no
            }))
        );

        if (this.settings.other.autoArrangeLayers) {
            this.products.sort(weatherSortFunction);
        }

        this.timeline.updateSettings(this.settings);
        this.mapLayerStack.updateSettings(this.settings, WeatherSettingsChangeSource.all);

        if (this.settings.animation.running) {
            this.timeline.startAnimation();
        }

        this.fireProductsChanged('loaded');
        this.fire('view.changed');
        // Use microtask, to give the consumers of the events above a chance to run
        queueMicrotask(() => {
            for (const prod of this.products) {
                this.loadProduct(prod);
            }
        });

        logger.debug('View loaded.');
    }

    refreshProducts(options?: { force: boolean }) {
        for (const prod of this.products) {
            const info = this.getProductLoadInfo(prod.id);
            // Check for updates
            if (
                options?.force ||
                (prod.weatherStore?.timestamp !== info?.current.timestamp1 && prod.weatherStore?.timestamp !== info?.current.timestamp2)
            ) {
                // Update if changed
                queueMicrotask(() => this.loadProduct(prod, true));
            }
        }
    }

    updateAltitude(value: number) {
        // NOTE: this doesn't consider global products because at the current time there's no grouped global product.
        this.altitudePicker.setAltitude(value);
        let modifiedProductList = false;
        for (const item of this.products) {
            if (item.product.group === null) {
                continue;
            }

            const newProduct = this.altitudePicker.pick(item.product.group);
            if (!newProduct) {
                // If we can't pick a new altitude leave the old product
                continue;
            }

            // If altitude picker selected the same product just continue
            if (newProduct.id === item.product.id) {
                continue;
            }
            // Remove old
            this.mapLayerStack?.removeProduct(item.id);
            // this.wms.removeProduct(item.id);

            const idx = this.products.indexOf(item);
            const newItem: SelectedWeatherItem = {
                id: newProduct.id,
                visible: item.visible,
                type: item.type,
                gdf: !shouldDisplayAsWms(newProduct),
                wms: shouldDisplayAsWms(newProduct),
                loaded: false,
                success: false,
                product: newProduct,
                updateLate: WeatherItemUpdateLate.no
            };
            this.products[idx] = newItem;
            if (newItem.wms) {
                //this.wms.addProduct(newItem.id);
            }

            // Keep original order
            queueMicrotask(() => this.loadProduct(newItem));
            modifiedProductList = true;
        }

        if (modifiedProductList) {
            this.fireProductsChanged('altitude');
        }
    }

    setFrameVisible(product: SelectedWeatherItem, frame: number) {
        const newFrame = product.weatherStore?.frames[frame];
        if (product.selectedFrame === newFrame) {
            return;
        }
        product.selectedFrame = newFrame;
        this.mapLayerStack?.showOneFrame(product.id, frame);
        // TODO: batch this event, it only need to be sent once
        this.fireProductsChanged('visibleFrameChanged');
    }

    removeAltitudeGroup(altitudeGroupId: number) {
        // There should be only one product in the group
        const toRemove = this.products.filter((x) => x.product.group === altitudeGroupId);
        for (const product of toRemove) {
            // This could spam addRemove event but since there only should be one product of this group this is fine
            this.removeProduct(product.id, product.type);
        }
    }

    showHideAllWeatherProducts(visible: boolean) {
        this.weatherVisibility = visible;
        for (const product of this.products) {
            // If there is a global or static version of this product, visibility is driven by those versions
            // Here we can't just check if product.type is global, because the list of this.products may contain multiple entries for the same product id but with different type
            // And the layer stack only has 1 type of product loaded at a given time.
            if (this.products.some((x) => x.id === product.id && x.type !== SelectedWeatherItemType.weather)) {
                continue;
            }

            if (product.gdf) {
                if (visible) {
                    this.mapLayerStack?.setVisibility(product.id, product.gdf && product.visible);
                } else {
                    this.mapLayerStack?.setVisibility(product.id, false);
                }
            }
        }

        //this.wms.setVisibility(visible);
    }

    showHideGdf(item: SelectedWeatherItem, visible: boolean) {
        if (!this.products.includes(item)) {
            return;
        }

        item.visible = visible;
        if (this.weatherVisibility) {
            if (item.gdf) {
                // If there's a global version of this product don't change visibility
                if (!this.products.some((x) => x.id === item.id && x.type === SelectedWeatherItemType.global)) {
                    this.mapLayerStack?.setVisibility(item.id, visible);
                }
            }
            if (item.wms) {
                if (visible) {
                    //this.wms.addProduct(id);
                } else {
                    //this.wms.removeProduct(id);
                }
            }
        }
        this.fireProductsChanged('visibility');
    }

    showHideWms(item: SelectedWeatherItem, visible: boolean) {
        if (!this.products.includes(item)) {
            return;
        }
        if (item) {
            item.wms = visible;
            if (this.weatherVisibility) {
                if (item.visible) {
                    if (visible) {
                        //this.wms.addProduct(id);
                    } else {
                        //this.wms.removeProduct(id);
                    }
                }
            }
        }
        this.fireProductsChanged('visibility');
    }

    moveProductUp(item: SelectedWeatherItem) {
        assert(this.mapLayerStack);
        const idx = this.products.indexOf(item);
        if (idx === -1 || idx === 0) {
            return;
        }
        const prod = this.products[idx];
        this.products[idx] = this.products[idx - 1];
        this.products[idx - 1] = prod;
        this.mapLayerStack.moveProduct(item.id, this.products[idx].id);
        this.fireProductsChanged('reordered');
    }

    moveProductDown(item: SelectedWeatherItem) {
        assert(this.mapLayerStack);
        const idx = this.products.indexOf(item);
        if (idx === -1 || idx === this.products.length - 1) {
            return;
        }

        const prod = this.products[idx];
        this.products[idx] = this.products[idx + 1];
        this.products[idx + 1] = prod;
        this.mapLayerStack.moveProduct(item.id, this.products[idx + 2]?.id);
        this.fireProductsChanged('reordered');
    }

    setWmsOpacity(value: number) {
        //this.wms.setOpacity(value);
    }

    async getFrame(id: number, frame: number): Promise<GDFFrame | null> {
        return await this.loader.getFrame(id, frame, this.workspaceId);
    }

    updateSettings(
        update: (settings: WeatherSettings) => void,
        changeSource: WeatherSettingsChangeSource = WeatherSettingsChangeSource.unknown
    ) {
        assert(this.settings);
        update(this.settings);

        this.mapLayerStack?.updateSettings(this.settings, changeSource);
        this.fire('settings.changed', { settings: this.settings, changeSource });

        // Keep timeline in sync too
        if (changeSource === WeatherSettingsChangeSource.animation) {
            this.timeline.updateSettings(this.settings);
        } else if (changeSource === WeatherSettingsChangeSource.autoArrange) {
            this.autoArrangeProducts();
        }
    }

    getSettings() {
        if (!this.settings) {
            throw new Error('Not initialized');
        }
        return this.settings;
    }

    getShowWeather(): boolean {
        return this.weatherVisibility;
    }

    onAirportFilterGroupsUpdated(filterGroup: FilterGroup[]) {
        logger.debug('Airport filter group updated', filterGroup);
        this.mapLayerStack?.updateAirportFilterGroups(filterGroup);
    }

    onSuaFilterGroupUpdated(filterGroup: FilterGroup[]) {
        logger.debug('SUA filter group updated', filterGroup);
        this.mapLayerStack?.updateSuaFilterGroup(filterGroup);
    }

    onMyAirportsUpdated(airports: string[]) {
        logger.debug('My airports changed', airports);
        this.mapLayerStack?.updateMyAirports(airports);
    }

    private async loadProduct(product: SelectedWeatherItem, update?: boolean) {
        if (!this.productIsAuthorized(product.id)) {
            product.loaded = true;
            product.success = false;
            product.error = 'Unauthorized';
            this.mapLayerStack?.removeProduct(product.id);
            //this.wms.removeProduct(id);
        } else {
            if (product.wms) {
                // If no GDF for this product just set loaded to true
                if (!product.gdf) {
                    product.loaded = true;
                }
                product.success = true;
                if (product.visible) {
                    //this.wms.addProduct(product.id);
                }
            }

            if (product.gdf) {
                const info = this.getProductLoadInfo(product.id);
                if (!info) {
                    product.loaded = true;
                    product.success = false;
                    product.error = 'Unavailable';
                    this.mapLayerStack?.removeProduct(product.id);
                    //this.wms.removeProduct(id);
                } else {
                    if (info.current.timestamp1 === null && info.current.timestamp2 === null) {
                        product.loaded = true;
                        product.success = false;
                        product.error = 'No timestamp available';
                        // Should the product be removed from display here? I don't think so.
                    } else {
                        const loadInfo = await this.loader.load(product.product, info);
                        // Check if the product has been removed during the time it was loading or layer manager was removed
                        if (!this.products.includes(product)) {
                            return;
                        }

                        product.loaded = true;
                        product.success = loadInfo.success;
                        product.error = loadInfo.error;
                        product.weatherStore = loadInfo;
                        product.updateLate = calculateUpdateLate(product, this.time.currentTime);
                        if (loadInfo.success) {
                            this.mapLayerStack?.addProduct(loadInfo);

                            // Get the first product in the list that is above the product and move the product below it
                            const idx = this.products.indexOf(product);
                            const firstLoaded = this.products.find((x, index) => index > idx && x.loaded && x.success);
                            if (firstLoaded) {
                                this.mapLayerStack?.moveProduct(product.id, firstLoaded.id);
                            }
                            this.mapLayerStack?.loadProduct(loadInfo);
                            // Global products are always visible
                            const isWeather = this.products.some((x) => x.id === product.id && x.type === SelectedWeatherItemType.weather);
                            const visibility = isWeather ? this.weatherVisibility && product.visible : true;
                            this.mapLayerStack?.setVisibility(product.id, visibility);
                            // Only move to the latest slide if the updated product was visible
                            this.timeline.updateProducts(this.products, { autoMoveToLatestSlide: !!update && visibility });
                        } else {
                            // should we remove product from display here?
                            this.mapLayerStack?.removeProduct(product.id);
                        }
                    }
                }
            }
        }
        this.fireProductsChanged('loaded');
    }

    private autoArrangeProducts() {
        if (this.settings?.other.autoArrangeLayers) {
            this.products.sort(weatherSortFunction);
            for (let i = this.products.length - 1; i >= 0; i--) {
                this.mapLayerStack?.moveProduct(this.products[i].id, this.products[i + 1]?.id);
            }
            this.fireProductsChanged('reordered');
        }
    }

    private fireProductsChanged(kind: ProductsChangedEventTypes) {
        this.fire('products.changed', { kind });
        if (kind === 'visibility' || kind === 'reordered') {
            this.timeline.updateProducts(this.products, { autoMoveToLatestSlide: false });
        }

        if (kind === 'addRemove' || kind === 'visibility' || kind === 'reordered') {
            this.fire('product.add-remove-visibility');
        }
    }

    private calculateLateProducts() {
        for (const product of this.products) {
            product.updateLate = calculateUpdateLate(product, this.time.currentTime);
        }
        this.fireProductsChanged('visibleFrameChanged');
    }
}

const wmsProds = new Set([
    // Ozone and advisories (future)
    0x6047, 0x6045
]);

function shouldDisplayAsWms(product: WeatherProduct): boolean {
    return wmsProds.has(product.id);
}

function calculateUpdateLate(item: SelectedWeatherItem, now: number): WeatherItemUpdateLate {
    if (!item.loaded || !item.weatherStore || !item.weatherStore.timestamp || !item.product.updatePeriodMinutes) {
        return WeatherItemUpdateLate.no;
    }

    const diffMinutes = (now - (item.weatherStore.timestamp + 631152000)) / 60;
    if (diffMinutes > 2.5 * item.product.updatePeriodMinutes) {
        return WeatherItemUpdateLate.red;
    }

    if (diffMinutes > 1.5 * item.product.updatePeriodMinutes) {
        return WeatherItemUpdateLate.yellow;
    }

    return WeatherItemUpdateLate.no;
}
